import {observer} from 'mobx-react-lite'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Time, Util, UUID} from 'yougile-util'
import {DataObjectId, stickersWithFilters, StickerType} from 'yougile-model'
import {Icon} from '@components/common/base/icon'
import {Sticker} from '@components/common/base/sticker/sticker'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {CustomIcons} from '@components/common/icon-tabler/custom-icons-16'
import {DraggableNative} from '@components/draggable-native'
import {useProjectStore} from '@components/project/project-store'
import {
  TwFilterBlock,
  TwStickerItemFilter
} from '@components/sticker/containers'
import {StickerItemTitle} from '@components/sticker/sticker-item-title'
import {StickerMenu} from '@components/sticker/sticker-menu/sticker-menu'
import {
  StickerContext,
  StickerStore,
  useStickerStore
} from '@components/sticker/sticker-store'
import {TimeTrackingStickerIcon} from '@components/sticker/time-tracking-sticker-icon'
import {
  StickerItemProps,
  StickerLocation,
  StickerWithMenuProps
} from '@components/sticker/types'
import {UserSticker} from '@components/sticker/user-sticker'
import {UserAvatar} from '@components/user-avatar'
import {useForceUpdate, useSyncEvent} from '@containers/hooks'
import {BoardFilters} from '@extensions/sticker/board-filters'
import {Task} from '@extensions/task'
import {taskPhantomObserver} from '@extensions/task-phantom-observer'

export const StickerItem = React.memo((props: StickerItemProps) => {
  const [stickerStore] = useState(() => new StickerStore(props.sticker, props))
  useEffect(() => {
    stickerStore.syncIsRunningTimer()
    return () => stickerStore.dispose()
  }, [])

  const forceUpdate = useForceUpdate()
  useEffect(() => {
    forceUpdate()
    stickerStore.setStickerTaskId(props.taskId)
  }, [props.taskId])

  useEffect(() => {
    if (props.hasError !== undefined) {
      stickerStore.setHasError(props.hasError)
    }
    if (props.menuIsOpened !== undefined) {
      stickerStore.setStickerMenuOpened(props.menuIsOpened)
    }
    if (props.required !== undefined) {
      stickerStore.syncStickerIsRequired(props.required)
    }
  }, [props.hasError, props.menuIsOpened, props.required])

  useEffect(() => {
    if (
      props.currentValue !== undefined ||
      stickerStore.stickerData.isAddingTask
    ) {
      stickerStore.setDefaultValue(props.currentValue)
    }
  }, [
    props.currentValue,
    stickerStore.stickerData.isAddingTask,
    stickerStore.stickerTask?.id
  ])

  useEffect(() => {
    stickerStore.syncParentLocation(props.parentLocation)
  }, [props.parentLocation])

  return (
    <StickerContext.Provider value={stickerStore}>
      <StickerItemBase />
    </StickerContext.Provider>
  )
})

const StickerItemBase = observer(() => {
  const [state, store] = useStickerStore()
  const [projectState] = useProjectStore()
  const {
    withTooltip,
    disabled,
    draggable,
    icon,
    withMenu,
    location,
    stickerType
  } = store.stickerData
  const filtersListener = useRef<UUID>()
  const isUserStickerOnTask = location === 'task' && stickerType === 'user'

  const forceUpdate = useForceUpdate()
  useEffect(() => {
    const isDeadline = stickerType === 'deadline'
    if (isDeadline) {
      const interval = setInterval(() => forceUpdate(), Time.minute)
      return () => clearInterval(interval)
    }
    return Util.noop
  }, [stickerType])

  useSyncEvent(
    'current',
    {filter: store.sticker?.id},
    () => {
      store.syncDefaultValue()
      store.syncStickerTitle()
    },
    [store.sticker?.id]
  )

  useSyncEvent(
    'current',
    {
      filter:
        store.stickerTask && Task.isTask(store.stickerTask)
          ? store.stickerTask.id
          : []
    },
    () => {
      store.syncDefaultValue()
      store.syncStickerHasTransparentStyle()
      store.syncTaskIsCompleted()
    },
    [state.taskId]
  )

  useSyncEvent(
    'current',
    {
      filter:
        store.stickerTask &&
        Task.isPhantom(store.stickerTask) &&
        store.stickerData.stickerType === 'deadline'
          ? store.stickerTask.id
          : []
    },
    () => {
      store.syncDefaultValue()
    },
    [state.taskId]
  )

  useSyncEvent(
    'current',
    {filter: projectState?.boardId || []},
    () => {
      store.syncOnUpdateBoard()
      store.syncIsRunningTimer()
    },
    [projectState?.boardId]
  )

  const taskDependenciesIds: DataObjectId[] = useMemo(
    () =>
      store.stickerData.stickerType === 'deadline'
        ? (Object.keys(
            store.stickerTask?.data.dependencies ?? {}
          ) as DataObjectId[])
        : [],
    [store.stickerData, state.taskId]
  )

  useSyncEvent(
    'current',
    {filter: taskDependenciesIds},
    store.syncDisplayedTitle,
    [taskDependenciesIds]
  )

  useEffect(() => {
    if (projectState?.boardId) {
      store.syncOnUpdateBoard()
      store.syncOnUpdateFilters()
    }
  }, [projectState?.boardId])

  useEffect(() => {
    store.isTaskTemplate && store.syncDefaultValue()
  }, [taskPhantomObserver.modified])

  useEffect(() => {
    if (location === 'panel') {
      store.syncOnUpdateFilters()
      filtersListener.current = BoardFilters.addListener(
        store.syncOnUpdateFilters
      )
    }
    return () => {
      if (filtersListener.current) {
        BoardFilters.removeListener(filtersListener.current)
      }
    }
  }, [location])

  const onClickOnSticker = () => {
    store.toggleStickerMenu()
  }

  const iconColor = `icon-${store.color}` as const
  const borderColor = store.color
  const bgColor = `icon-${store.color}` as const

  return (
    <WithMenu
      withMenu={withMenu}
      isEditable={state.isEditable}
      onCreateMenu={store.onCreateMenu}
      isOpened={Boolean(state.menuIsOpened)}
      onToggle={store.tryPinAndCloseMenu}
    >
      <DraggableNative
        cloneElement
        onDragStarted={store.startDragging}
        onDragStartedCallback={store.tryPinAndCloseMenu}
        onDragEnded={store.endDragging}
        transfer={store.dragData}
      >
        <div data-menu-is-opened={state.menuIsOpened}>
          {isUserStickerOnTask && (
            <UserSticker
              className={store.className}
              required={state.required}
              isDraggable={Boolean(draggable)}
              onClick={onClickOnSticker}
            />
          )}
          {!isUserStickerOnTask && (
            <Sticker
              color={borderColor}
              iconColor={iconColor}
              iconBackgroundColor={bgColor}
              className={store.className}
              isDraggable={Boolean(draggable)}
              isDragging={state.isDragging}
              showDraggableStyle={false}
              icon={getStickerItemIcon({
                icon,
                location,
                stickerType,
                filterValues: state.filterValues,
                noValue: store.noValue
              })}
              title={<StickerItemTitle />}
              active={store.isActive}
              disabled={disabled}
              onClick={onClickOnSticker}
              rightIcon={getStickerRightIcon({
                location,
                stickerType,
                withMenu,
                filterValues: state.filterValues
              })}
              required={state.required}
              hasError={state.hasError}
              hint={
                withTooltip && state.value ? (
                  <span className="flex items-center text-inherit-recursive">
                    {state.settings?.titleBeforeValue ? (
                      state.displayedTitle
                    ) : (
                      <>
                        {store.name}: {state.displayedTitle}
                      </>
                    )}
                  </span>
                ) : undefined
              }
            />
          )}
        </div>
      </DraggableNative>
    </WithMenu>
  )
})

const WithMenu = observer((props: StickerWithMenuProps) => {
  const {withMenu, isEditable, children, isOpened, onToggle, onCreateMenu} =
    props

  if (!withMenu || !isEditable) {
    return <>{children}</>
  }

  return (
    <Dropdown
      visible={isOpened}
      onVisibleChange={onToggle}
      content={<StickerMenu />}
      offset={[0, 6]}
      animation={false}
      arrow
      onCreate={onCreateMenu}
    >
      {children}
    </Dropdown>
  )
})

interface StickerItemIconProps {
  stickerType: StickerType
  filterValues?: string[]
  noValue: boolean
  location: StickerLocation
  icon?: CustomIcons
}
const getStickerItemIcon = ({
  stickerType,
  filterValues,
  noValue,
  location,
  icon
}: StickerItemIconProps): JSX.Element | CustomIcons | undefined => {
  if (stickerType === 'user' && filterValues?.length === 1) {
    const userId = filterValues[0] as DataObjectId
    return (
      <div className="flex p-2">
        <UserAvatar size={16} userId={userId} />
      </div>
    )
  }

  if (stickerType === 'sprint') {
    return !filterValues?.length && noValue
      ? 'IconCustomCalendarEvent'
      : undefined
  }

  if (location === 'task' && stickerType === 'timetracking') {
    return <TimeTrackingStickerIcon />
  }

  return icon
}

interface StickerRightIcon {
  location: StickerLocation
  stickerType: StickerType
  withMenu: boolean
  filterValues?: string[]
}
const getStickerRightIcon = ({
  location,
  stickerType,
  withMenu,
  filterValues
}: StickerRightIcon): undefined | JSX.Element => {
  if (
    location === 'task' ||
    !stickersWithFilters.includes(stickerType) ||
    !withMenu
  ) {
    return undefined
  }

  const filtersAmount = stickerType !== 'deadline' && filterValues?.length
  const filtersAmountVisible = filtersAmount && filtersAmount > 1

  return (
    <>
      {Boolean(filtersAmountVisible) && (
        <TwStickerItemFilter>{filtersAmount}</TwStickerItemFilter>
      )}
      {!filtersAmountVisible && (
        <TwFilterBlock $filtersLength={filterValues?.length}>
          <Icon size={16} icon="IconCustomStickerFilterBack" />
        </TwFilterBlock>
      )}
    </>
  )
}
